import { useMemo, useState } from 'react';
import { Icon, Spinner } from './ui/Icon.js';
import { Segmented } from './ui/Segmented.js';
import { EmptyState } from './EmptyState.js';
import type { CollectionMatrixRow } from '../types/index.js';
import { ENGINE_LABELS } from '../types/index.js';

const ENGINES = ['chatgpt', 'gemini', 'claude', 'google_aio', 'mistral'] as const;

interface ExecutionMatrixProps {
  matrix: CollectionMatrixRow[];
  onTestPrompt: (promptTemplateId: string) => Promise<void>;
  loading: boolean;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function statusColor(lastRun: string | null, lastStatus: string | null): string {
  if (!lastRun) return 'bg-gray-300'; // never run
  if (lastStatus === 'failed') return 'bg-red-500';
  // Check staleness: > 3 days = yellow
  const diff = Date.now() - new Date(lastRun).getTime();
  const threeDays = 3 * 24 * 60 * 60 * 1000;
  if (diff > threeDays) return 'bg-yellow-400';
  return 'bg-green-500';
}

export function ExecutionMatrix({ matrix, onTestPrompt, loading }: ExecutionMatrixProps) {
  const [testingRow, setTestingRow] = useState<string | null>(null);

  // Region dimension — union of every template's region codes, sorted. When a
  // brand is multi-region this holds ≥2 codes and drives the region toggle.
  const regionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of matrix) {
      for (const code of row.regions ?? []) set.add(code);
    }
    return Array.from(set).sort();
  }, [matrix]);

  const regionAware = regionOptions.length >= 2;
  // Default to the first region alphabetically.
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const activeRegion =
    regionAware && selectedRegion && regionOptions.includes(selectedRegion)
      ? selectedRegion
      : regionOptions[0] ?? null;

  const handleTest = async (promptTemplateId: string) => {
    setTestingRow(promptTemplateId);
    try {
      await onTestPrompt(promptTemplateId);
    } finally {
      setTestingRow(null);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white flex items-center justify-center py-12">
        <Spinner className="text-violet-500" />
      </div>
    );
  }

  if (matrix.length === 0) {
    return (
      // Spec 250 (review P1-7): the one empty state, and its fix is a link.
      <EmptyState
        icon="bx-grid-alt"
        title="No active prompts"
        description="The execution matrix fills in once prompts are set up."
        actionLabel="Set up prompts"
        actionHref="/prompts"
        actionVariant="ghost"
        testId="execution-matrix-empty"
      />
    );
  }

  return (
    <div className="overflow-x-auto p-5" data-testid="execution-matrix">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-900">Execution matrix</h3>
        {regionAware && (
          // Spec 249 (review P1-11): the region is a parameter of the matrix,
          // so Kridt's Segmented control; the group keeps its testid, which
          // gate 192.C looks for in the bundle.
          <Segmented
            options={regionOptions.map((code) => ({ value: code, label: code }))}
            value={activeRegion}
            onChange={setSelectedRegion}
            label="Filter matrix by region"
            testId="collection-region-toggle"
          />
        )}
      </div>
      {/* Active-region caption — an always-visible, toggle-driven signal so the
          region switch is legible even when two regions have identical job
          structure (the cells alone can look unchanged). */}
      {regionAware && activeRegion && (
        <p className="mb-3 text-xs text-slate-500" data-testid="collection-region-active">
          Showing the{' '}
          <span className="font-semibold text-violet-700">{activeRegion}</span>{' '}
          region — each cell below reflects captures pinned to {activeRegion}. Use
          the toggle to switch regions.
        </p>
      )}
      <table className="hub-table">
        <thead>
          <tr>
            <th>Prompt</th>
            {ENGINES.map((engine) => (
              // Spec 234: the colour-keyed cell background is retired — the
              // column header already names the engine, so colour was
              // decoration standing in for the label that was already there.
              <th key={engine} className="text-center">
                {ENGINE_LABELS[engine] ?? engine}
              </th>
            ))}
            <th className="text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {matrix.map((row) => (
            <tr key={row.prompt_template_id}>
              <td className="max-w-[300px]">
                <p className="truncate" title={row.template_snippet}>
                  {row.template_snippet}
                </p>
                <span className="hub-chip mt-0.5">
                  {row.category}
                </span>
              </td>
              {ENGINES.map((engine) => {
                // Region-aware view reads the selected region's per-engine cell;
                // otherwise the cross-region aggregate (byte-identical to before).
                const engineSource =
                  regionAware && activeRegion
                    ? row.by_region?.[activeRegion] ?? {}
                    : row.engines;
                const cell = engineSource[engine];
                const failCount = cell?.fail_count ?? 0;
                const pendingCount = cell?.pending_count ?? 0;
                const sampleCount = cell?.sample_count ?? 0;
                const lastError = cell?.last_error ?? null;
                const tooltip = lastError
                  ? `Last error: ${lastError}${failCount > 1 ? ` (${failCount} failed)` : ''}`
                  : failCount > 0
                    ? `${failCount} failed`
                    : pendingCount > 0
                      ? `${pendingCount} pending`
                      : undefined;
                return (
                  <td key={engine} className="text-center">
                    <div className="flex flex-col items-center gap-0.5" title={tooltip}>
                      <span className={`inline-block h-2 w-2 rounded-full ${statusColor(cell?.last_run ?? null, cell?.last_status ?? null)}`} />
                      <span className="text-xs text-[var(--hub-color-ink-muted)]">{timeAgo(cell?.last_run ?? null)}</span>
                      <span className="text-xs text-[var(--hub-color-ink-subtle)]">
                        n={sampleCount}
                        {failCount > 0 && <span className="text-red-500 ml-0.5">✗{failCount}</span>}
                        {pendingCount > 0 && <span className="text-amber-500 ml-0.5">⏳{pendingCount}</span>}
                      </span>
                    </div>
                  </td>
                );
              })}
              <td className="text-right">
                <button
                  onClick={() => handleTest(row.prompt_template_id)}
                  disabled={testingRow === row.prompt_template_id}
                  className="hub-btn hub-btn--ghost"
                  data-testid={`test-prompt-${row.prompt_template_id}`}
                >
                  {testingRow === row.prompt_template_id ? (
                    <Spinner />
                  ) : (
                    <Icon name="bx-test-tube" />
                  )}
                  Test
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
